import api from "./api";
import {
  UserInvitationCreate,
  UserInvitationResponse,
  RegistrationTokenInfo,
  UserRegistrationComplete,
  UserRegistrationCompleteResponse,
} from "../types/registrationTypes";

/**
 * Registration Service
 *
 * Handles user registration API endpoints including:
 * - Admin user invitations
 * - Registration token validation
 * - User registration completion
 */

const registration_base = "/auth";

export const registrationService = {
  /**
   * Create User Invitation (Admin only)
   * POST /api/v1/auth/invite-user
   * 
   * @param invitationData - User invitation details
   * @returns Promise<UserInvitationResponse>
   */
  inviteUser: async (
    invitationData: UserInvitationCreate
  ): Promise<UserInvitationResponse> => {
    try {
      const response = await api.post(
        `${registration_base}/invite-user`,
        invitationData
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.detail || "Failed to send invitation"
      );
    }
  },

  /**
   * Validate Registration Token
   * GET /api/v1/auth/invitation/{token}
   * 
   * @param token - Registration token from email link
   * @returns Promise<RegistrationTokenInfo>
   */
  validateToken: async (token: string): Promise<RegistrationTokenInfo> => {
    try {
      const response = await api.get(`${registration_base}/invitation/${token}`);
      return response.data;
    } catch (error: any) {
      // For token validation, we want to return the error info rather than throw
      // This allows the component to handle invalid tokens gracefully
      if (error.response?.status === 404 || error.response?.status === 400) {
        return {
          valid: false,
          error: error.response?.data?.detail || "Invalid registration token"
        };
      }
      throw new Error(
        error.response?.data?.detail || "Failed to validate token"
      );
    }
  },

  /**
   * Complete User Registration
   * POST /api/v1/auth/complete-registration/{token}
   * 
   * @param token - Registration token from email link
   * @param registrationData - Password and confirmation
   * @returns Promise<UserRegistrationCompleteResponse>
   */
  completeRegistration: async (
    token: string,
    registrationData: UserRegistrationComplete
  ): Promise<UserRegistrationCompleteResponse> => {
    try {
      const response = await api.post(
        `${registration_base}/complete-registration/${token}`,
        registrationData
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.detail || "Failed to complete registration"
      );
    }
  },
};